import type { AssignedOutletResource, OutletResource } from '@repo/api-client';

/**
 * The two lists this screen reads, merged into the one thing it renders.
 *
 * Kept out of the component because the merge is where the ticket's defect
 * actually lives, and a pure function is where it can be reasoned about — and,
 * when the switch flips back on, tested — without a render.
 *
 * **`via` and `assigned` are different questions and they are kept apart here
 * deliberately.** `via` answers *may they act here*: for an owner or a manager
 * it is `ROLE` at every live outlet, because their reach is by role and no row
 * is needed. `assigned` answers *is there actually a row*. For outlet and gate
 * staff the two always agree; for owners and managers they do not, and it is the
 * second one — never the first — that a client sends back.
 */

/** One live outlet, as this screen shows it against one person. */
export interface AssignmentRow {
  readonly outlet: OutletResource;

  /**
   * How this person reaches the outlet's records, or `undefined` where they do
   * not reach them at all. Display only: nothing derives the submitted set from
   * it.
   */
  readonly via: AssignedOutletResource['via'] | undefined;

  /** Whether an assignment row exists. **This is what prefills, and what is sent.** */
  readonly assigned: boolean;
}

/**
 * Every live outlet, against what `GET /v1/users/{id}/outlets` said about this
 * person.
 *
 * The outlets come from `GET /v1/outlets` and not from the reach list, because
 * the reach list is not the universe: for staff it carries only the outlets they
 * already hold, so a screen built from it alone could show what to remove and
 * never what to add. For owners and managers it *is* every live outlet, and the
 * merge simply agrees with itself.
 *
 * Both routes filter to live outlets server-side, so nothing retired can enter
 * here — which is what makes a prefill safe to send straight back. An outlet
 * retired between the read and the save is refused by the API rather than
 * dropped (FR-109), and this screen surfaces that refusal.
 */
export function mergeAssignments(
  outlets: readonly OutletResource[],
  reach: readonly AssignedOutletResource[],
): AssignmentRow[] {
  const byId = new Map(reach.map((item) => [item.id, item]));

  return outlets.map((outlet) => {
    const found = byId.get(outlet.id);

    return {
      outlet,
      via: found?.via,
      // `?? false`, and not `found !== undefined`. An outlet can appear in the
      // reach list with no row behind it — that is precisely the owner-and-
      // manager case — so presence in that list is not evidence of an
      // assignment, and reading it as such is the defect this screen is about.
      assigned: found?.assigned ?? false,
    };
  });
}

/**
 * The outlets a person actually holds a row for, as the set the form prefills
 * with and the set it submits.
 *
 * One function for both, called at both ends, so "what was prefilled" and "what
 * is sent" cannot be computed two different ways. That is the whole shape of the
 * bug: a screen that prefilled from one field and submitted another looked
 * correct and rostered an owner onto every till in the park.
 */
export const assignedOutletIds = (rows: readonly { id: string; assigned: boolean }[]) =>
  new Set(rows.filter((row) => row.assigned).map((row) => row.id));

/**
 * Whether this person reaches every live outlet by role, read off the API's own
 * answer rather than off their role.
 *
 * FR-111 is the server's rule and `via` is the server stating it. Restating it
 * here as "OWNER or MANAGER" would be a second copy of a business rule that this
 * screen has no need to know, and would be wrong the day a fifth role is added.
 */
export const reachesByRole = (rows: readonly AssignmentRow[]): boolean =>
  rows.some((row) => row.via === 'ROLE');
